// @flow

import React, { useEffect, useState, useRef } from "react";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import Ajv from "ajv";
import type { State } from "@store/reducers";
import classNames from "classnames";
import { getEmployeePositions } from "@modules/shared/selectors/employee-positions";
import {
  selectBankHolidays,
  isCaptureBankHolidaysEnabled,
  isAbsenceDetailsLoading,
  isLoading,
} from "@modules/shared/selectors/employee-absences";
import { getServiceSettings } from "@modules/settings/selectors";
import { removeNullAndUndefinedProps } from "@transformers/object";
import {
  ShiftPatternTable,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  SchemaForm,
  Icons,
  withFocusFirstInput,
  Loader,
  View,
} from "@components";
import type { EmployeeAbsence } from "@modules/shared/reducer/employee-absences/types";
import type { EmployeeType } from "@modules/employee/types";
import type { RoleSummary } from "@modules/employee/reducer/employment-details/types";
import sharedActionCreators from "@modules/shared/actionCreator";
import moment from "moment";

type PropsFromState = $Exact<{
  employeePositions: RoleSummary[],
  serviceSettings: Object,
  isLoading: boolean,
  bankHolidays: Array<string>,
  captureBankHolidays: boolean | null,
}>;

type PropsFromDispatch = $Exact<{
  updateAbsence: typeof sharedActionCreators.UpdateAbsence,
  createAbsence: typeof sharedActionCreators.CreateAbsence,
}>;

type Props = {
  onDismissHandler: () => void,
  onSubmit: (form: Object) => void,
  onChange: (form: Object) => void,
  clientId: number,
  schema: Object | null,
  editing?: boolean,
  absence: EmployeeAbsence,
  absenceId: number | null,
  employee: EmployeeType,

  shiftPatternDays: any,
  timeLostShiftPattern: any,
  bankHolidays: any,
  captureBankHolidays: any,
  showRepeatButton: any,
  ...PropsFromLocalize,
  ...PropsFromState,
  ...PropsFromDispatch,
};

const ModalBodyWithFocus = withFocusFirstInput(ModalBody);
const datePickerOptions = {
  enableTime: true,
  dateFormat: "DD/MM/YYYY HH:mm",
  placeholder: "dd/mm/yyyy hh:mm",
};

function AddEmployeeAbsenceModal(props: Props) {
  const { serviceSettings, schema, absence } = props;
  const formRef = useRef(null);

  const initialDates = absence || {
    startsAt: moment().format(),
    fitAt: moment()
      .add(1, "days")
      .format(),
    rtwAt: moment()
      .add(1, "days")
      .format(),
  };

  const [isValidAbsenceData, setIsValidAbsenceData] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(initialDates);
  const [uiSchema, setUiSchema] = useState(processUISchema(initialDates));

  const [absenceChanged, setAbsenceChanged] = useState(false);

  const tlSp = props.timeLostShiftPattern;
  const [timeLostShiftPattern, setTimeLostShiftPattern] = useState(props.timeLostShiftPattern);
  const [isValidTimeLossData, setIsValidTimeLossData] = useState(null);

  const startsAt = formData.startsAt;
  const absenceType = formData.absenceType;
  const absenceReason = formData.absenceReason;

  useEffect(() => {
    setUiSchema(processUISchema(formData));
  }, [formData]);

  useEffect(() => {
    if (absence) {
      setAbsenceChanged(true);
      setFormData(absence);
      setUiSchema(processUISchema(absence));
    }
  }, [absence]);
  useEffect(() => {
    setTimeLostShiftPattern(tlSp);
  }, [tlSp]);

  function processUISchema(data) {
    const minStartsAt = moment((props.employee || {}).joinDate);
    const minFitAt = (data.startsAt ? moment(data.startsAt) : minStartsAt)
      .add(1, "minutes")
      .format();

    return {
      id: { "ui:widget": "hidden" },
      isHistoricAbsence: { "ui:widget": "hidden" },
      createdAt: { "ui:widget": "hidden" },

      startsAt: {
        "ui:widget": "date-picker",
        "ui:options": {
          ...datePickerOptions,
          minDate: minStartsAt.format(),
        },
        "ui:title": "Absence Start Date",
      },

      fitAt: {
        "ui:widget": "date-picker",
        "ui:options": {
          ...datePickerOptions,
          minDate: minFitAt,
        },
        "ui:title": "Fit Date and Time",
      },
      rtwAt: {
        "ui:widget": "date-picker",
        "ui:options": {
          ...datePickerOptions,
          minDate: minFitAt,
        },
        "ui:title": "RTW Date and Time",
      },

      hasConsentToShare: { "ui:widget": "toggle" },
      isWorkRelated: { "ui:widget": "toggle" },
      isAccidentRelated: { "ui:widget": "toggle" },

      "ui:order": [
        "status",
        "absenceType",
        "absenceReason",
        "hasConsentToShare",
        "startsAt",
        "reportedAt",
        "fitAt",
        "rtwAt",
        "*",
      ],
    };
  }

  function handleAbsenceDataChange(data: Object, errorSchema) {
    let startsAt = data.startsAt ? moment(data.startsAt) : undefined;
    let fitAt = data.fitAt ? moment(data.fitAt) : undefined;
    let rtwAt = data.rtwAt ? moment(data.rtwAt) : undefined;

    if (startsAt) {
      if ((fitAt && startsAt.isSame(fitAt)) || (fitAt && startsAt.isAfter(fitAt))) {
        fitAt = moment(startsAt.format());
        fitAt.add(1, "minutes");
        rtwAt = fitAt;
      }
      if (fitAt && rtwAt && fitAt.isAfter(rtwAt)) {
        rtwAt = fitAt;
      }
    }

    const fixedData = {
      ...data,
      startsAt: startsAt ? startsAt.format() : undefined,
      fitAt: fitAt ? fitAt.format() : undefined,
      rtwAt: rtwAt ? rtwAt.format() : undefined,
    };

    // Letc check if absence reason has valid data (probably can be done thru schema form's default...)

    if (fixedData.absenceType && fixedData.absenceReason) {
      const rrr = {
        MEDICAL:
          schema?.dependencies.absenceType.oneOf[0].properties.absenceReason.properties.id.oneOf,
        NON_MEDICAL:
          schema?.dependencies.absenceType.oneOf[1].properties.absenceReason.properties.id.oneOf,
      };
      const reasons = rrr[fixedData.absenceType];
      const isValidReasonSelected = reasons.find(
        reason => reason.const === fixedData.absenceReason.id,
      );
      if (!isValidReasonSelected) {
        fixedData.absenceReason.id = reasons[0].const;
      }
    }

    setFormData(fixedData);
    setUiSchema(processUISchema(fixedData));
    setIsValidAbsenceData(formRef.current && formRef.current.validateFormWithFormData(fixedData));
  }

  function onSubmit() {
    formRef.current.submit();
  }
  function handleAbsenceSubmit() {
    if (!formData.id || (absenceChanged === true && formData.id)) {
      setAbsenceChanged(false);
    }
    setActiveStep(1);
  }

  function handleSubmitAbsence() {
    if (formData.hasOwnProperty("id")) {
      props.updateAbsence({
        clientId: props.clientId,
        employeeId: props.employee.id,
        absenceId: formData.id,
        formData: formData,
      });
    } else {
      props.createAbsence({
        clientId: props.clientId,
        employeeId: props.employee.id,
        formData: formData,
      });
    }
    props.onDismissHandler();
  }

  function handleStepEdit(stepNum: number) {
    return function(e) {
      setActiveStep(stepNum);
    };
  }

  function handleTimeLossChange(timeLost) {
    handleAbsenceDataChange({ ...formData, timeLost: timeLost });
  }

  function statusIcon(val: typeof undefined | null | boolean) {
    if (val == undefined || val == null) return <Icons.StepCircleIcon color={"black"} />;
    return val === true ? (
      <Icons.StepCheckIcon color="positive" />
    ) : (
      <Icons.StepCircleIcon color="negative" />
    );
  }

  function getModalTitle() {
    return formData?.id ? `${props.t("EDIT_ABSENCE")} ${formData.id}` : props.t("ADD_ABSENCE");
  }

  function handleTimeLossValidate(valid) {
    setIsValidTimeLossData(valid);
  }
  const activeEmployeePositions: RoleSummary[] = (props.employeePositions || []).filter(
    i => i?.status === "ACTIVE",
  );

  const getSchema = () => {
    const absenceType = props.serviceSettings?.absenceType;
    if (
      schema &&
      absenceType &&
      (absenceType === "MEDICAL_ONLY" || absenceType === "NON_MEDICAL_ONLY")
    ) {
      const allowedOption = absenceType === "MEDICAL_ONLY" ? "MEDICAL" : "NON_MEDICAL";
      return {
        ...schema,
        properties: {
          ...schema.properties,
          absenceType: {
            ...schema.properties.absenceType,
            oneOf: schema.properties.absenceType.oneOf.filter(el => el.const === allowedOption),
          },
        },
      };
    }

    return schema;
  };

  return (
    <Modal className="modal-large">
      <ModalHeader toggle={props.onDismissHandler}>{getModalTitle()}</ModalHeader>
      <ModalBodyWithFocus className="max-height-70vh">
        {/* 1/2 Absence Details */}
        <div
          className={classNames("step", {
            active: activeStep === 0,
            valid: isValidAbsenceData === true,
            invalid: isValidAbsenceData === false,
          })}
        >
          <View direction="row" justifyContent="space-between" className="header ">
            <View direction="row">
              {statusIcon(isValidAbsenceData)}
              <h5>1/2 {props.t("ABSENCE_DETAILS")}</h5>
            </View>
            <View>
              {activeStep !== 0 && (
                <span
                  onClick={handleStepEdit(0)}
                  className="edit cursor-pointer material-icons fs-32"
                >
                  edit
                </span>
              )}
            </View>
          </View>
          <div className="body">
            <SchemaForm
              ref={formRef}
              formData={formData}
              onSubmit={handleAbsenceSubmit}
              onChange={handleAbsenceDataChange}
              schema={getSchema()}
              uiSchema={uiSchema}
              externalSubmit
              liveValidate={false}
            />
          </div>
          <div className="footer pt-4">
            <div className="d-flex justify-content-end">
              <Button
                materialIcon={"fast_forward"}
                onClick={onSubmit}
                disabled={isValidAbsenceData !== true}
              >
                {props.t("NEXT")}
              </Button>
            </div>
          </div>
        </div>
        {/* 2/2 Shift Pattern */}
        <div
          className={classNames("step", {
            active: activeStep === 1,
            valid: isValidTimeLossData === true,
            invalid: isValidTimeLossData === false,
          })}
        >
          <div className="header ">
            {statusIcon(isValidTimeLossData)}
            <h5>2/2 {props.t("SHIFT_PATTERN")}</h5>
            {activeStep !== 1 &&
              isValidAbsenceData === true &&
              isValidTimeLossData !== null &&
              formData?.id && (
                <span
                  onClick={handleStepEdit(1)}
                  className="edit cursor-pointer material-icons fs-32"
                >
                  edit
                </span>
              )}
          </div>
          {activeStep === 1 && (
            <div className="body">
              <ShiftPatternTable
                absence={formData}
                employeePositions={activeEmployeePositions.map(ep => ({
                  value: ep.id,
                  label: ep.jobTitle,
                }))}
                onValidate={handleTimeLossValidate}
                onChange={handleTimeLossChange}
                timeLostShiftPattern={timeLostShiftPattern}
                shiftPatternDaysConfig={
                  activeEmployeePositions[0]?.shiftPattern?.shiftPatternDays || []
                }
                bankHolidays={props.bankHolidays}
                captureBankHolidays={props.captureBankHolidays}
                minimumAbsenceDuration={serviceSettings.minimumAbsenceDuration}
                showRepeatButton
              />
            </div>
          )}
        </div>
      </ModalBodyWithFocus>
      <ModalFooter className="d-flex justify-content-end">
        <Button color="secondary" onClick={props.onDismissHandler}>
          {props.t("CANCEL")}
        </Button>

        <Button onClick={handleSubmitAbsence} materialIcon="save" disabled={!isValidTimeLossData}>
          {props.t("SAVE")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

const mapStateToProps = (state: State, ownProps: Object): PropsFromState => {
  return {
    serviceSettings: getServiceSettings(state),
    bankHolidays: selectBankHolidays(state),
    captureBankHolidays: isCaptureBankHolidaysEnabled(state),
    employeePositions: getEmployeePositions(state),
    isLoading: isAbsenceDetailsLoading(state) || isLoading(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  updateAbsence: sharedActionCreators.UpdateAbsence,
  createAbsence: sharedActionCreators.CreateAbsence,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(AddEmployeeAbsenceModal));
